import type { GrowthTable, UnitDef } from "../combat/types";
import { ENEMY_PALETTES, PLAYER_PALETTES } from "../art/palettes";

// ---- Player growth tables -----------------------------------------------
// Each value is the % chance for that stat to gain +1 on level up. See
// docs/RAVAGE_DESIGN.md §4.3 for the per-character philosophy. Hero
// characters (Amar) get the highest totals; supports (Ranatoli, Ning)
// trade total points for specialization.
//
// Co-located here rather than scattered into each factory so cross-unit
// balance is comparable at a glance.

const G_AMAR:     GrowthTable = { hp: 70, power: 60, armor: 40, speed: 50, movement: 10 };
const G_LUCIAN:   GrowthTable = { hp: 75, power: 50, armor: 60, speed: 25, movement: 5 };
const G_NING:     GrowthTable = { hp: 50, power: 60, armor: 25, speed: 60, movement: 15 };
const G_MAYA:     GrowthTable = { hp: 50, power: 55, armor: 30, speed: 70, movement: 20 };
const G_LEO:      GrowthTable = { hp: 65, power: 60, armor: 50, speed: 45, movement: 10 };
// Ranatoli's power growth sits above pure-support so his rejoin at the
// fleet arc (caught up ~10 levels past his authored L10) still chips
// meaningfully through late armor. He remains a shieldbearer, not a
// striker — the sim's strike-core metric excludes him for kill speed.
const G_RANATOLI: GrowthTable = { hp: 80, power: 50, armor: 70, speed: 25, movement: 5 };
const G_SELENE:   GrowthTable = { hp: 60, power: 70, armor: 35, speed: 65, movement: 15 };
// Veya trades body for beam: highest power growth on the roster, worst
// hp/armor. Her lens already halves enemy armor (Damage.ts), so her
// damage curve stays flat and reliable — the growths just keep the raw
// number relevant while everyone else's plate thickens.
const G_VEYA:     GrowthTable = { hp: 45, power: 70, armor: 15, speed: 45, movement: 5 };
// Corin: professional cavalry — balanced soldier growths, a touch of
// everything, nothing spiked. His identity lives in the mount (+2 MOV
// from mountBonus) and, post-promotion, the Khan's first-strike Charge,
// not in any single stat outgrowing the roster.
const G_CORIN:    GrowthTable = { hp: 65, power: 55, armor: 45, speed: 50, movement: 10 };
const G_KIAN:     GrowthTable = { hp: 65, power: 55, armor: 55, speed: 50, movement: 10 };
// Rose joins the squad at B13 (Madame Dawn's Rebellion) as Dawn's
// most senior lieutenant — quick, deadly, devoted. Dies in
// post_dawn_rebellion (mechanically she survives B13; the death is
// narrated in the post arc, same pattern Lucian uses at B11).
// Single-battle appearance for the playable slice — growths matter
// only if she's revived in a future commit. High speed + crit
// matches her shinobi class kit.
const G_ROSE:     GrowthTable = { hp: 55, power: 60, armor: 30, speed: 75, movement: 20 };

// Generic enemy growth tables. Used when a battle author actively chooses
// to give an enemy growths (rare — most enemies are single-encounter). The
// rank-and-file enemy factories leave growths undefined so they don't
// level up mid-battle.
const G_BANDIT:   GrowthTable = { hp: 55, power: 50, armor: 30, speed: 40, movement: 5 };
const G_ROYAL:    GrowthTable = { hp: 65, power: 55, armor: 60, speed: 45, movement: 5 };

export const PLAYERS = {
  amar: (): UnitDef => ({
    id: "amar",
    name: "Amar",
    shortName: "Am",
    faction: "player",
    classKind: "swordsman",
    weapon: "sword",
    // Hero-of-the-story bump: a touch more HP, hit, and footwork than the rest.
    // Post-amnesia statline (see amarHidden for the original-coup version).
    stats: { hp: 38, power: 12, armor: 5, speed: 9, movement: 5, ap: 3 },
    artSeed: 1,
    palette: PLAYER_PALETTES.amar,
    portrait: true,
    abilities: ["BossFighter"],
    // Post-amnesia Amar starts at L1 — the script's "muscle memory intact,
    // raw stats reset" is now a true reset. The player's progression,
    // not the factory default, decides how strong he is. The save's
    // CharacterRecord overrides this on every battle after his first.
    level: 1,
    growths: G_AMAR
  }),
  amarHidden: (): UnitDef => ({
    // Same Amar, but with the "true" combat statline (used in Battle 1 — pre-amnesia).
    id: "amar_true",
    name: "Amar",
    shortName: "Am",
    faction: "player",
    classKind: "swordsman",
    weapon: "sword",
    stats: { hp: 44, power: 15, armor: 6, speed: 11, movement: 5, ap: 3 },
    artSeed: 1,
    palette: PLAYER_PALETTES.amar,
    portrait: true,
    portraitId: "amar",
    abilities: ["BossFighter"],
    // Pre-amnesia Amar is a level-10 veteran — same as the rest of the
    // original coup squad. Doesn't matter for B1 progression (the coup
    // is scripted to fail) but enforces the lore baseline.
    level: 10,
    growths: G_AMAR
  }),
  lucian: (): UnitDef => ({
    id: "lucian",
    name: "Lucian",
    shortName: "Lu",
    faction: "player",
    classKind: "spearton",
    weapon: "spear",
    stats: { hp: 36, power: 11, armor: 6, speed: 5, movement: 3, ap: 2 },
    artSeed: 2,
    palette: PLAYER_PALETTES.lucian,
    portrait: true,
    abilities: ["Aide"],
    level: 1,
    growths: G_LUCIAN
  }),
  ning: (): UnitDef => ({
    id: "ning",
    name: "Ning",
    shortName: "Ni",
    faction: "player",
    classKind: "archer",
    weapon: "bow",
    stats: { hp: 22, power: 9, armor: 3, speed: 9, movement: 4, ap: 2 },
    artSeed: 3,
    palette: PLAYER_PALETTES.ning,
    portrait: true,
    abilities: ["Aide"],
    level: 1,
    growths: G_NING
  }),
  maya: (): UnitDef => ({
    id: "maya",
    name: "Maya",
    shortName: "Ma",
    faction: "player",
    classKind: "shinobi",
    weapon: "sword",
    stats: { hp: 26, power: 10, armor: 3, speed: 11, movement: 5, ap: 3 },
    artSeed: 4,
    palette: PLAYER_PALETTES.maya,
    portrait: true,
    abilities: ["Aide"],
    // Maya joins at L1 — the player's progression decides her curve, not
    // a factory bump. (Earlier versions started her at L2 to reflect her
    // narrative training under Dawn; that's now expressed via the
    // catch-up rule when she joins late, not via the factory.)
    level: 1,
    growths: G_MAYA
  }),
  leo: (): UnitDef => ({
    id: "leo",
    name: "Leo",
    shortName: "Le",
    faction: "player",
    classKind: "dactyl_rider",
    weapon: "dactyl",
    stats: { hp: 30, power: 11, armor: 5, speed: 8, movement: 5, ap: 3 },
    artSeed: 5,
    palette: PLAYER_PALETTES.leo,
    portrait: true,
    abilities: ["Destruct", "Roam"],
    // Leo joins at L1. The script casts him as Fergus's trained son but
    // mechanically he starts where every other post-amnesia recruit
    // starts — the player's level curve decides how strong he gets,
    // not a factory bump. The catch-up rule applies if he joins late
    // relative to the squad's average.
    level: 1,
    growths: G_LEO
  }),
  ranatoli: (): UnitDef => ({
    id: "ranatoli",
    name: "Ranatoli",
    shortName: "Ra",
    faction: "player",
    classKind: "sentinel",
    weapon: "shield",
    stats: { hp: 40, power: 8, armor: 8, speed: 4, movement: 3, ap: 2 },
    artSeed: 6,
    palette: PLAYER_PALETTES.ranatoli,
    portrait: true,
    // Destruct makes finishing him expensive; Mend (L10 support — his
    // baseline IS L10, so it's live from the moment he rejoins) lets a
    // slow shield veteran earn XP by keeping the line standing.
    abilities: ["Destruct", "Mend"],
    // Original-8 coup veteran. Uses L10 baseline; the catch-up rule in
    // Progression.catchUpToSquad will fast-forward him further if the
    // squad has out-leveled L10 by the time he rejoins.
    level: 10,
    growths: G_RANATOLI
  }),
  selene: (): UnitDef => ({
    id: "selene",
    name: "Selene",
    shortName: "Se",
    faction: "player",
    classKind: "swordmaster",
    weapon: "sword",
    stats: { hp: 30, power: 13, armor: 4, speed: 12, movement: 4, ap: 3 },
    artSeed: 7,
    palette: PLAYER_PALETTES.selene,
    portrait: true,
    // Selene is already a Swordmaster (Tier 2) when the squad meets her
    // at the monastery in B7. L10 baseline + catch-up rule applies.
    level: 10,
    growths: G_SELENE
  }),
  veya: (): UnitDef => ({
    id: "veya",
    name: "Veya",
    shortName: "Ve",
    faction: "player",
    classKind: "lenscaster",
    weapon: "lens",
    // Fragile artillery: range 2-3 beam that halves armor (Damage.ts),
    // priced with the worst hp/armor/movement on the roster and 2 AP.
    // She kills through plate; she cannot hold a doorway.
    stats: { hp: 24, power: 11, armor: 2, speed: 7, movement: 3, ap: 2 },
    artSeed: 10,
    palette: PLAYER_PALETTES.veya,
    portrait: true,
    abilities: ["Aide"],
    // No lenscaster sprite folder yet — the shinobi's cloaked silhouette
    // stands in for her coat-and-apron figure until bespoke art ships.
    spriteClassOverride: "shinobi",
    // Joins at B14 (Grude, after Dawn's rebellion). L1 like every other
    // recruit — the catch-up rule fast-forwards her to the squad's
    // average the moment she's first fielded.
    level: 1,
    growths: G_VEYA
  }),
  corin: (): UnitDef => ({
    id: "corin",
    name: "Corin",
    shortName: "Co",
    faction: "player",
    classKind: "knight",
    weapon: "spear",
    // The squad's first mounted ground unit: knight classKind carries
    // the +2 mountBonus (6 effective movement), spear reaches 1-2, and
    // Roam extends the ride after the lance work is done. Solid but not
    // tanky — cavalry dies when it stands still.
    stats: { hp: 32, power: 11, armor: 5, speed: 8, movement: 4, ap: 3 },
    artSeed: 11,
    palette: PLAYER_PALETTES.corin,
    portrait: true,
    abilities: ["Roam"],
    // No knight sprite folder yet — the spearton's lance silhouette
    // stands in until cavalry art ships (same pattern as Kian).
    spriteClassOverride: "spearton",
    // Joins at B17 (the quay, breaking with Dawn's line). L1 like every
    // recruit — catch-up fast-forwards him on first fielding.
    level: 1,
    growths: G_CORIN
  }),
  kian: (): UnitDef => ({
    id: "kian",
    name: "Kian",
    shortName: "Ki",
    faction: "player",
    classKind: "knight",
    sprite: "kian",
    // No knight sprite folder under public/assets/sprites/ yet. Render as
    // swordmaster (visually distinct from Amar's swordsman, fits Kian's
    // "elite blade in the king's service" framing) until proper knight
    // sprites ship. The "knight" classKind is preserved so Kian still
    // gets the +2 mountBonus from Actions.ts.
    spriteClassOverride: "swordmaster",
    weapon: "sword",
    stats: { hp: 32, power: 11, armor: 6, speed: 7, movement: 4, ap: 3 },
    artSeed: 8,
    palette: PLAYER_PALETTES.kian,
    portrait: true,
    // Kian joins at L1. Same reasoning as Maya/Leo — progression is
    // earned through play, not authored into the factory default.
    level: 1,
    growths: G_KIAN
  }),
  // Rose — Madame Dawn's senior lieutenant, joins the squad for B13
  // only (Madame Dawn's Rebellion). Mechanically a shinobi: high
  // speed, low armor, blade-and-blade. Dies in post_dawn_rebellion
  // (mechanically she survives B13; the death is narrated in the
  // post arc, same pattern Lucian uses at B11). Joins at L13 — the
  // squad's around L13-15 by Grude, and the catch-up rule will
  // bump her further if needed.
  rose: (): UnitDef => ({
    id: "rose",
    name: "Rose",
    shortName: "Ro",
    faction: "player",
    classKind: "shinobi",
    sprite: "rose",
    weapon: "sword",
    stats: { hp: 36, power: 12, armor: 4, speed: 14, movement: 5, ap: 3 },
    artSeed: 9,
    palette: PLAYER_PALETTES.rose,
    portrait: true,
    level: 13,
    growths: G_ROSE
  })
};

// ---- Enemy factories ----------------------------------------------------
// Every enemy factory takes an explicit `level` parameter so per-battle
// authors can tune difficulty in the call site. The level feeds the XP
// reward formula (level diff modifier in Progression.xpRewardFor) and is
// the hook the future stat-scaling pass will multiply against. For the
// v1 system stats are still hand-authored — battle authors override the
// `stats` field on the returned object if they want a tougher specimen.

// Generic enemy classes share a single portrait per class — every Bandit
// instance shows the same `bandit.png`, every Royal Guard the same
// `royal_guard.png`, etc. portraitId routes the per-instance unit.id
// (e.g., "dawn_sw1") to the shared portrait file via PortraitArt's
// resolution path. portrait: true marks them as eligible for portrait
// rendering at all (the side panel checks this).
export const ENEMIES = {
  banditSwordsman: (id: string, seed: number, level = 2): UnitDef => ({
    id,
    name: "Bandit",
    shortName: "Bd",
    faction: "enemy",
    classKind: "swordsman",
    sprite: "bandit_swordsman",
    weapon: "sword",
    stats: { hp: 22, power: 8, armor: 3, speed: 6, movement: 4, ap: 3 },
    statReferenceLevel: 2,
    artSeed: seed,
    palette: ENEMY_PALETTES.bandit,
    portrait: true,
    portraitId: "bandit",
    level,
    growths: G_BANDIT
  }),
  banditArcher: (id: string, seed: number, level = 2): UnitDef => ({
    id,
    name: "Raider",
    shortName: "Rd",
    faction: "enemy",
    classKind: "archer",
    sprite: "bandit_archer",
    weapon: "bow",
    stats: { hp: 18, power: 7, armor: 2, speed: 7, movement: 4, ap: 2 },
    statReferenceLevel: 2,
    artSeed: seed,
    palette: ENEMY_PALETTES.bandit_archer,
    portrait: true,
    portraitId: "raider",
    level,
    growths: G_BANDIT
  }),
  banditSpearton: (id: string, seed: number, level = 3): UnitDef => ({
    id,
    name: "Reaver",
    shortName: "Rv",
    faction: "enemy",
    classKind: "spearton",
    sprite: "bandit_spearton",
    weapon: "spear",
    stats: { hp: 26, power: 10, armor: 5, speed: 5, movement: 3, ap: 2 },
    statReferenceLevel: 3,
    artSeed: seed,
    palette: ENEMY_PALETTES.bandit,
    portrait: true,
    portraitId: "reaver",
    level,
    growths: G_BANDIT
  }),
  // Royal classes are tagged "elite" — Progression.xpRewardFor treats
  // elite kills as 50 XP base instead of the default 30.
  royalGuard: (id: string, seed: number, level = 6): UnitDef => ({
    id,
    name: "Royal Guard",
    shortName: "RG",
    faction: "enemy",
    classKind: "spearton",
    sprite: "royal_guard",
    weapon: "spear",
    stats: { hp: 30, power: 11, armor: 7, speed: 6, movement: 3, ap: 2 },
    statReferenceLevel: 6,
    artSeed: seed,
    palette: ENEMY_PALETTES.royal_guard,
    portrait: true,
    portraitId: "royal_guard",
    tags: new Set(["elite"]),
    level,
    growths: G_ROYAL
  }),
  royalArcher: (id: string, seed: number, level = 6): UnitDef => ({
    id,
    name: "Crown Archer",
    shortName: "Ca",
    faction: "enemy",
    classKind: "archer",
    sprite: "crown_archer",
    weapon: "bow",
    stats: { hp: 22, power: 9, armor: 3, speed: 8, movement: 4, ap: 2 },
    statReferenceLevel: 6,
    artSeed: seed,
    palette: ENEMY_PALETTES.royal_guard,
    portrait: true,
    portraitId: "crown_archer",
    tags: new Set(["elite"]),
    level,
    growths: G_ROYAL
  }),
  // Bosses don't have growths — they're single-encounter. Battle authors
  // pass an explicit level if they want to override the default.
  ndari: (level = 7): UnitDef => ({
    id: "ndari",
    name: "Ndari",
    shortName: "Nd",
    faction: "enemy",
    classKind: "boss",
    sprite: "ndari",
    weapon: "sword",
    stats: { hp: 56, power: 13, armor: 6, speed: 9, movement: 4, ap: 3 },
    artSeed: 99,
    palette: ENEMY_PALETTES.ndari,
    portrait: true,
    tags: new Set(["boss"]),
    level
  }),
  kingNebu: (level = 12): UnitDef => ({
    id: "nebu",
    name: "King Nebu IV",
    shortName: "Ne",
    faction: "enemy",
    classKind: "boss",
    sprite: "nebu",
    weapon: "sword",
    stats: { hp: 70, power: 14, armor: 8, speed: 7, movement: 3, ap: 3 },
    artSeed: 88,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    tags: new Set(["boss"]),
    level,
    // Sits on the throne and refuses to engage until only one of his
    // guards remains alive. Reflects the script's framing of B1: the
    // King doesn't expect to need to fight — he expects his guard to
    // repel the assault, and only stirs once the line is nearly broken.
    // The AI early-returns "end" until the threshold is crossed.
    holdPositionUntil: { allyCount: 1 }
  }),
  // Selene as antagonist in Battle 7 (the monastery). She's one of Amar's
  // original coup comrades from B1, surfaced here on a King's wanted poster
  // and leading raiders out of an abandoned monastery in the high passes.
  // Mechanically a swordmaster (Tier 2 already — she joined the original
  // coup pre-promotion-system, narratively a veteran). Tagged "boss" so she
  // gives 100 base XP and triggers the BossFighter bonus when applicable.
  //
  // Distinct unit id ("selene_enemy") so progression saved here doesn't
  // collide with PLAYERS.selene if she's recruited in a future battle —
  // they're the same character fictionally but separate save records.
  // Shares the player portrait via portraitId so the side-panel avatar
  // and dialog scenes route to the same artwork.
  selene: (level = 11): UnitDef => ({
    id: "selene_enemy",
    name: "Selene",
    shortName: "Se",
    faction: "enemy",
    classKind: "swordmaster",
    weapon: "sword",
    stats: { hp: 48, power: 14, armor: 5, speed: 13, movement: 5, ap: 3 },
    artSeed: 7,
    palette: PLAYER_PALETTES.selene,
    portrait: true,
    portraitId: "selene",
    tags: new Set(["boss"]),
    level
  }),
  // Kian as enemy boss for B10 (Thuling streets) + B11 (Cliffs). He
  // was a player unit in B4-B9; from B10 onward he's revealed as
  // Nebu's enforcer and turns on the squad. Distinct id ("kian_enemy")
  // mirrors the Selene pattern — separate save record so any future
  // path that re-recruits him doesn't collide. Portrait shared via
  // portraitId so the side-panel + dialog scenes route to the same
  // artwork. Stats slightly above Selene's because Kian is the
  // mechanical hard-stop on the player's escape, with a +mountBonus
  // from the knight class.
  // Captain Volos — Archbold's interception officer at Grude harbor.
  // First named enemy of the second-half (post-crossing) campaign.
  // Distinct from the Anthros royal-guard line: he's an empire
  // officer, slightly higher level than B11's elites, holds the
  // marble customs platform with the holdPositionUntil pattern so
  // his line gets thinned before he engages personally.
  archboldCaptain: (level = 13): UnitDef => ({
    id: "archbold_captain",
    name: "Captain Volos",
    shortName: "Vo",
    faction: "enemy",
    classKind: "swordmaster",
    weapon: "sword",
    stats: { hp: 50, power: 14, armor: 8, speed: 11, movement: 4, ap: 3 },
    artSeed: 21,
    // Reuse Kian's palette as a stand-in until a Volos portrait /
    // palette ships. Visually the player will read him as "another
    // tall officer in royal kit," which matches the narrative
    // (Archbold's empire officers carry themselves the same way as
    // Anthros's; that's part of the colony reveal).
    palette: PLAYER_PALETTES.kian,
    portrait: true,
    portraitId: "kian",
    tags: new Set(["boss"]),
    level,
    // Holds the customs platform until 2 of his line are still up,
    // mirroring B1 King Nebu / B10-B11 Kian. Gives the player time
    // to engage the line and gives the round-1 dialogue room to
    // land before Volos personally engages.
    holdPositionUntil: { allyCount: 2 }
  }),
  // Royal Captain — boss-tier officer for Archbold's nephew's
  // residence guard at B13 (Madame Dawn's Rebellion). Higher level
  // than the squad's earlier royal-tier encounters because B13
  // takes place after the Grude crossing — the squad has been
  // gone for a year, the King's elite have hardened in the
  // meantime. Holds position until the guard is thinned (mirrors
  // King Nebu / Kian patterns) so the round-1 dialogue lands
  // before swords meet.
  royalCaptain: (level = 14): UnitDef => ({
    id: "royal_captain",
    name: "Royal Captain",
    shortName: "Cp",
    faction: "enemy",
    classKind: "boss",
    weapon: "sword",
    stats: { hp: 60, power: 15, armor: 8, speed: 10, movement: 4, ap: 3 },
    artSeed: 77,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "royal_guard",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  // Lord Castor — Knight-Captain of King Archbold's household guard,
  // sent personally to retrieve Amar once the empire learns the
  // Anthros coup-leader is the emperor's own hidden child (B14, The
  // Origin). Highest-statline officer the squad has faced — he's
  // household elite, not provincial garrison. Holds position until
  // his line is thinned so the round-1 dialogue lands first.
  imperialKnight: (level = 15): UnitDef => ({
    id: "imperial_knight",
    name: "Lord Castor",
    shortName: "Cs",
    faction: "enemy",
    classKind: "boss",
    sprite: "castor",
    weapon: "sword",
    stats: { hp: 64, power: 16, armor: 9, speed: 11, movement: 4, ap: 3 },
    statReferenceLevel: 11,
    artSeed: 41,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "castor",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  // Quartermaster Coyne — Madame Dawn's Grude safe-house quartermaster,
  // secretly King Archbold's mole; the leak that put Castor's detail
  // on the safe house at B14 (A Coup Within a Coup). An intelligence
  // officer, not a frontline knight — lower HP/armor than Castor or
  // the royal captains, but fast (a survivor's statline). Holds the
  // courtyard's back gate until his line is thinned.
  turncoat: (level = 14): UnitDef => ({
    id: "turncoat",
    name: "Quartermaster Coyne",
    shortName: "Co",
    faction: "enemy",
    classKind: "boss",
    weapon: "sword",
    stats: { hp: 48, power: 13, armor: 6, speed: 13, movement: 5, ap: 3 },
    statReferenceLevel: 9,
    artSeed: 53,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "coyne",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  // Wren, the King's Knife — King Archbold's household assassin, sent
  // at B16 once the empire decides a living heir is worse than a dead
  // one. An assassin's statline: the fastest unit in the game (speed
  // 16 — acts early, can trigger speed-counters), high power, but
  // fragile (low HP + armor). NO holdPositionUntil — unlike the
  // garrison bosses, Wren rushes straight for Amar. classKind "boss"
  // so Amar's BossFighter ability + boss XP apply; the assassin
  // flavour lives in the statline + dialogue, not the class tag.
  kingsKnife: (level = 16): UnitDef => ({
    id: "kings_knife",
    name: "Wren",
    shortName: "Wr",
    faction: "enemy",
    classKind: "boss",
    sprite: "wren",
    weapon: "sword",
    stats: { hp: 46, power: 15, armor: 5, speed: 16, movement: 6, ap: 3 },
    statReferenceLevel: 10,
    artSeed: 67,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "wren",
    tags: new Set(["boss"]),
    level
  }),
  // Marshal Othren — Madame Dawn's Grude garrison commander, and a
  // true believer in her plan. At B17 (Dawn's Lie) he blocks the
  // squad's break for the harbour: a heavy, defensive officer who
  // forms a line and holds it. Not empire — one of Dawn's own — so
  // he carries the bandit (irregular/rebel) palette rather than the
  // archbold one. Spear + shield-tier armour; holds position until
  // his line is thinned.
  dawnLoyalist: (level = 15): UnitDef => ({
    id: "dawn_loyalist",
    name: "Marshal Othren",
    shortName: "Ot",
    faction: "enemy",
    classKind: "boss",
    sprite: "othren",
    weapon: "spear",
    stats: { hp: 58, power: 14, armor: 9, speed: 9, movement: 4, ap: 3 },
    statReferenceLevel: 10,
    artSeed: 88,
    palette: ENEMY_PALETTES.bandit,
    portrait: true,
    portraitId: "othren",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  kian: (level = 12): UnitDef => ({
    id: "kian_enemy",
    name: "Kian",
    shortName: "Ki",
    faction: "enemy",
    classKind: "knight",
    sprite: "kian",
    // Same sprite override as PLAYERS.kian — knight has no shipped
    // sprite folder, falls back to swordmaster.
    spriteClassOverride: "swordmaster",
    weapon: "sword",
    stats: { hp: 52, power: 15, armor: 7, speed: 9, movement: 4, ap: 3 },
    artSeed: 8,
    palette: PLAYER_PALETTES.kian,
    portrait: true,
    portraitId: "kian",
    tags: new Set(["boss"]),
    level,
    // Kian holds his blockade position until his guard is thinned —
    // mirrors B1 King Nebu's pattern. Won't break ranks and engage
    // until only 2 of his 6 royal guards are still alive on the
    // field. Narrative: he wants to talk first; combat is a last
    // resort. Mechanical: the player can choose to thin the guard
    // and fight him (full XP), OR rush past him on B10's escape
    // win condition. On B11 the only path is through, but the
    // hold-position buys the round-1 colony-truth dialogue time
    // to land before swords meet.
    holdPositionUntil: { allyCount: 2 }
  }),
  // General Serrick — Archbold's field general for the war's opening
  // clash (B20, Dawn's War). A career line-officer, not a duelist:
  // heavy armour, an anchored command position, and a guard line he
  // won't leave until it's thinned. Killing him folds the imperial
  // line — B20's win condition.
  imperialGeneral: (level = 18): UnitDef => ({
    id: "imperial_general",
    name: "General Serrick",
    shortName: "Sr",
    faction: "enemy",
    classKind: "boss",
    sprite: "serrick",
    weapon: "sword",
    stats: { hp: 72, power: 17, armor: 10, speed: 10, movement: 4, ap: 3 },
    statReferenceLevel: 13,
    artSeed: 97,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "serrick",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  // Captain Halden — vanguard commander of Archbold's westward advance
  // (B21). The opposite of Serrick: no hold-position, he leads the
  // push from the front rank. Spear reach + high movement makes him
  // the pressure the survive-clock is measured against.
  vanguardCaptain: (level = 17): UnitDef => ({
    id: "vanguard_captain",
    name: "Captain Halden",
    shortName: "Hd",
    faction: "enemy",
    classKind: "boss",
    weapon: "spear",
    stats: { hp: 60, power: 16, armor: 8, speed: 12, movement: 5, ap: 3 },
    statReferenceLevel: 11,
    artSeed: 101,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "royal_guard",
    tags: new Set(["boss"]),
    level
  }),
  // Captain Brask — leader of the incendiary teams burning the upper
  // district (B22, Grude Burns). A terror-war specialist: fast, lightly
  // armoured, directs from the fountain square until his teams are
  // thinned. The fight is a street-by-street reclamation with him as
  // the rot at the centre.
  incendiaryCaptain: (level = 17): UnitDef => ({
    id: "incendiary_captain",
    name: "Captain Brask",
    shortName: "Bk",
    faction: "enemy",
    classKind: "boss",
    weapon: "sword",
    stats: { hp: 56, power: 15, armor: 7, speed: 13, movement: 5, ap: 3 },
    statReferenceLevel: 12,
    artSeed: 103,
    palette: ENEMY_PALETTES.bandit,
    portrait: true,
    portraitId: "brask",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 3 }
  }),

  // ---- The Ravage fleet (endgame arc, B25+) --------------------------------
  // Off-world soldiery. Deliberately beefier base statlines than any
  // human rank-and-file: the fleet is the threat the whole campaign has
  // been underestimating, and the numbers say so on first contact. No
  // portraits — the fleet doesn't show its faces, and the absence reads
  // as intended, not missing.
  ravageTrooper: (id: string, seed: number, level = 19): UnitDef => ({
    id,
    name: "Ravage Trooper",
    shortName: "Rv",
    faction: "enemy",
    classKind: "swordsman",
    sprite: "ravage_trooper",
    weapon: "sword",
    stats: { hp: 42, power: 19, armor: 8, speed: 9, movement: 4, ap: 3 },
    statReferenceLevel: 19,
    artSeed: seed,
    palette: ENEMY_PALETTES.ravage,
    tags: new Set(["elite"]),
    level
  }),
  ravageLancer: (id: string, seed: number, level = 19): UnitDef => ({
    id,
    name: "Ravage Lancer",
    shortName: "Rl",
    faction: "enemy",
    classKind: "spearton",
    sprite: "ravage_lancer",
    weapon: "spear",
    stats: { hp: 44, power: 19, armor: 9, speed: 8, movement: 4, ap: 3 },
    statReferenceLevel: 19,
    artSeed: seed,
    palette: ENEMY_PALETTES.ravage,
    tags: new Set(["elite"]),
    level
  }),
  ravageMarksman: (id: string, seed: number, level = 19): UnitDef => ({
    id,
    name: "Ravage Marksman",
    shortName: "Rm",
    faction: "enemy",
    classKind: "archer",
    sprite: "ravage_marksman",
    weapon: "bow",
    stats: { hp: 30, power: 17, armor: 4, speed: 11, movement: 4, ap: 2 },
    statReferenceLevel: 19,
    artSeed: seed,
    palette: ENEMY_PALETTES.ravage,
    tags: new Set(["elite"]),
    level
  }),
  // Herald of the Ravage — the commander's first officer, sent down in
  // B27 to see what held the shore. Falls there; the commander itself
  // is met in B28 (restoration/duty finales).
  ravageHerald: (level = 19): UnitDef => ({
    id: "ravage_herald",
    name: "Herald of the Ravage",
    shortName: "He",
    faction: "enemy",
    classKind: "boss",
    sprite: "herald",
    weapon: "sword",
    stats: { hp: 70, power: 17, armor: 10, speed: 12, movement: 5, ap: 3 },
    statReferenceLevel: 15,
    artSeed: 107,
    palette: ENEMY_PALETTES.ravage,
    portrait: true,
    portraitId: "herald",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  ravageCommander: (level = 20): UnitDef => ({
    id: "ravage_commander",
    name: "The Ravage Commander",
    shortName: "Cm",
    faction: "enemy",
    classKind: "boss",
    sprite: "ravage_commander",
    weapon: "sword",
    stats: { hp: 85, power: 19, armor: 11, speed: 12, movement: 5, ap: 3 },
    statReferenceLevel: 15,
    artSeed: 109,
    palette: ENEMY_PALETTES.ravage,
    portrait: true,
    portraitId: "ravage_commander",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),

  // ---- Path-climax humans (B23/B24/B28) ------------------------------------
  // Colonel Vasse — commander of the imperial remnant in the narrows
  // (B23). A survivor of Serrick's broken line, fighting on because
  // stopping would mean the war was for nothing.
  remnantColonel: (level = 18): UnitDef => ({
    id: "remnant_colonel",
    name: "Colonel Vasse",
    shortName: "Va",
    faction: "enemy",
    classKind: "boss",
    weapon: "sword",
    stats: { hp: 62, power: 16, armor: 9, speed: 10, movement: 4, ap: 3 },
    statReferenceLevel: 12,
    artSeed: 111,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "vasse",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  // Warden Sarto — keeper of the bell court (B24). A shield wall of one:
  // the slowest, heaviest boss in the game, guarding the last muster
  // bell in the west.
  bellWarden: (level = 18): UnitDef => ({
    id: "bell_warden",
    name: "Warden Sarto",
    shortName: "Sa",
    faction: "enemy",
    classKind: "boss",
    weapon: "shield",
    stats: { hp: 68, power: 15, armor: 12, speed: 8, movement: 3, ap: 3 },
    statReferenceLevel: 13,
    artSeed: 113,
    palette: ENEMY_PALETTES.royal_guard,
    portrait: true,
    portraitId: "sarto",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  // King Archbold — the emperor himself, the final opponent of the
  // vengeance and mercy paths (B28). The campaign's whole gravity in
  // one statline; the two paths differ in what victory MEANS, not in
  // how hard he hits.
  archbold: (level = 20): UnitDef => ({
    id: "archbold",
    name: "King Archbold",
    shortName: "Ar",
    faction: "enemy",
    classKind: "boss",
    sprite: "archbold",
    weapon: "sword",
    stats: { hp: 88, power: 19, armor: 11, speed: 11, movement: 4, ap: 3 },
    statReferenceLevel: 15,
    artSeed: 115,
    palette: ENEMY_PALETTES.archbold,
    portrait: true,
    portraitId: "archbold",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  }),
  // Madame Dawn — the revolution path's final opponent (B28). Burning
  // every throne includes the one your mother built out of thirty years
  // of arithmetic. Fast, deliberate, and the hardest fight to WANT.
  dawnBoss: (level = 20): UnitDef => ({
    id: "dawn_boss",
    name: "Madame Dawn",
    shortName: "Dw",
    faction: "enemy",
    classKind: "boss",
    sprite: "dawn",
    weapon: "sword",
    stats: { hp: 78, power: 18, armor: 9, speed: 13, movement: 5, ap: 3 },
    statReferenceLevel: 14,
    artSeed: 117,
    palette: ENEMY_PALETTES.ndari,
    portrait: true,
    portraitId: "dawn",
    tags: new Set(["boss"]),
    level,
    holdPositionUntil: { allyCount: 2 }
  })
};
